/**
 * Which registered till this tablet is.
 *
 * The server keeps one cash drawer per till, and finds the right one by the
 * till's id. A request that names no till gets whichever drawer at the site was
 * opened last — right for a site with one till, and quietly wrong with two:
 * each takes cash into the other's drawer, and both reconcile against the
 * wrong takings at close. So a tablet is told once which till it is, and every
 * drawer call and payment from it says so.
 *
 * Remembered on the device, not the account. It is a fact about the tablet
 * bolted to the bar — whoever signs in on it is standing at that till — and it
 * survives a sign-out at the end of a shift.
 *
 * One store, subscribed to by every screen that asks. The header, the terminal
 * sheet and the pay screen all need the answer, and a copy each would leave
 * the header saying "Which till?" after somebody had just picked one.
 *
 * A site with no registered tills has one drawer for the whole site, which is
 * the honest answer there and is what `none-registered` means. A remembered
 * till that has since been retired is still honoured: the server lets a retired
 * till keep its drawer, so taking a till off the plan never strands the cash in
 * it mid-service.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { useTerminals, type Terminal } from '@restaurant/api-client';

export type TillState =
  | { kind: 'loading' }
  | { kind: 'none-registered' }
  | { kind: 'unchosen'; choices: Terminal[] }
  | { kind: 'chosen'; till: Terminal; choices: Terminal[] };

const keyFor = (locationId: string) => `pos.till.${locationId}`;
const listeners = new Set<() => void>();

function readStored(locationId: string | undefined): string | null {
  if (!locationId) return null;
  try {
    return localStorage.getItem(keyFor(locationId));
  } catch {
    return null;
  }
}

function writeStored(locationId: string, terminalId: string | null): void {
  try {
    if (terminalId) localStorage.setItem(keyFor(locationId), terminalId);
    else localStorage.removeItem(keyFor(locationId));
  } catch {
    // A browser that refuses storage just asks again next time. Nothing is lost.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab on the same device changing it counts too.
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

export function useThisTill(locationId: string | undefined): {
  state: TillState;
  /** The till to send, or undefined for a site-wide drawer or an unanswered question. */
  terminalId: string | undefined;
  choose: (terminalId: string | null) => void;
} {
  // Retired tills included, so a remembered one still resolves.
  const terminals = useTerminals(locationId, true);
  const stored = useSyncExternalStore(
    subscribe,
    () => readStored(locationId),
    () => null,
  );

  const choose = useCallback(
    (terminalId: string | null) => {
      if (locationId) writeStored(locationId, terminalId);
    },
    [locationId],
  );

  if (!locationId || terminals.isPending) {
    return { state: { kind: 'loading' }, terminalId: undefined, choose };
  }

  const all = terminals.data ?? [];
  if (all.length === 0) {
    return { state: { kind: 'none-registered' }, terminalId: undefined, choose };
  }

  const choices = all.filter((t) => t.is_active);
  const till = all.find((t) => t.id === stored) ?? null;
  if (till === null) {
    return { state: { kind: 'unchosen', choices }, terminalId: undefined, choose };
  }
  return { state: { kind: 'chosen', till, choices }, terminalId: till.id, choose };
}

/** Whether a drawer can be looked up: either the till is known, or there are none. */
export function tillSettled(state: TillState): boolean {
  return state.kind === 'chosen' || state.kind === 'none-registered';
}
